import { TouchableOpacity, View, useWindowDimensions, Pressable, Platform, ScrollView } from "react-native";
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '@constants/Themed';
import styles from "@stylesheet";
import LottieView from '@utils/lottieAnimation';
import { HIDE_ALL_MODALS, HIDE_AUTH_MODAL, HIDE_LOADING_MODAL, REFRESH } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import React, { useEffect, useState, useCallback, useRef } from "react";
import Button from '../../UI/Button/Button';
import * as Animatable from 'react-native-animatable';
import ErrorBoundary from "@utils/ErrorBoundary";
import { Text, XStack, YStack, Progress, Circle, Dialog } from "tamagui";
import { useNavigation } from "@react-navigation/native";
import { useResponsiveWeb } from '@hooks/useResponsiveWeb';
import MarkDown from 'react-native-markdown-display';
import { rules } from '@utils/stringUtils/markDownRules';

const AuthModal = (props) => {
  const authModal = useSelector((state: DefaultRootState) => state.modals?.authModal);
  const visibleState = useSelector((state: DefaultRootState) => state.modals?.authModal?.modalDisplayed ?? false);
  const theme = useSelector((state: DefaultRootState) => state.app.theme?.lightTheme);
  const windowDimensions = useWindowDimensions();
  const { scaling, breakpoints, getResponsiveFontSize } = useResponsiveWeb();
  
  // Modern color palette
  const colors = {
    background: theme ? '#FFFFFF' : '#1A1B23',
    surface: theme ? '#F8FAFC' : '#2A2D3A',
    border: theme ? '#E2E8F0' : '#3F434A',
    text: {
      primary: theme ? '#0F172A' : '#F1F5F9',
      secondary: theme ? '#475569' : '#94A3B8',
      tertiary: theme ? '#64748B' : '#64748B',
    },
    status: {
      success: '#10B981',
      error: '#EF4444',
      warning: '#F59E0B',
      info: '#3B82F6',
      progress: '#06B6D4',
    },
    shadow: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.3)',
    backdrop: 'rgba(0, 0, 0, 0.5)',
  };

  // Modern dynamic styles
  const modernStyles = {
    modalContainer: {
      backgroundColor: theme ? "white" : "black",
      borderRadius: 24 * scaling.scaleFactor,
      padding: 32 * scaling.scaleFactor,
      paddingVertical: 40 * scaling.scaleFactor,
      minWidth: Math.max(320, 380 * scaling.scaleFactor),
      maxWidth: Math.min(windowDimensions.width * 0.9, 500 * scaling.scaleFactor),
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 20 },
      shadowOpacity: 0.25,
      shadowRadius: 25,
      elevation: 20,
      borderWidth: 1,
      borderColor: colors.border,
    },
    iconContainer: {
      minHeight: 120 * scaling.scaleFactor,
      marginBottom: 24 * scaling.scaleFactor,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
    },
    contentContainer: {
      gap: 20 * scaling.scaleFactor,
      alignItems: 'center' as const,
    },
    messageText: {
      fontSize: getResponsiveFontSize(16),
      lineHeight: getResponsiveFontSize(24),
      fontWeight: '600' as const,
      color: colors.text.primary,
      textAlign: 'center' as const,
      letterSpacing: -0.2,
    },
    progressContainer: {
      width: '100%' as const,
      gap: 12 * scaling.scaleFactor,
      alignItems: 'center' as const,
      backgroundColor: colors.surface,
      padding: 20 * scaling.scaleFactor,
      borderRadius: 16 * scaling.scaleFactor,
      borderWidth: 1,
      borderColor: colors.border,
    },
    progressText: {
      fontSize: getResponsiveFontSize(14),
      fontWeight: '500' as const,
      color: colors.text.secondary,
    },
    statusText: {
      fontSize: getResponsiveFontSize(13),
      fontStyle: 'italic' as const,
      color: colors.text.tertiary,
      textAlign: 'center' as const,
    },
    detailsContainer: {
      width: '100%' as const,
      gap: 16 * scaling.scaleFactor,
    },
    detailsToggle: {
      paddingVertical: 12 * scaling.scaleFactor,
      paddingHorizontal: 16 * scaling.scaleFactor,
      borderRadius: 12 * scaling.scaleFactor,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    detailsToggleText: {
      fontSize: getResponsiveFontSize(14),
      fontWeight: '500' as const,
      color: colors.status.info,
      textAlign: 'center' as const,
    },
    detailsContent: {
      backgroundColor: colors.surface,
      borderRadius: 16 * scaling.scaleFactor,
      padding: 20 * scaling.scaleFactor,
      borderWidth: 1,
      borderColor: colors.border,
      gap: 16 * scaling.scaleFactor,
    },
    detailsText: {
      fontSize: getResponsiveFontSize(12),
      lineHeight: getResponsiveFontSize(18),
      color: colors.text.secondary,
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    },
    buttonContainer: {
      flexDirection: 'row' as const,
      gap: 12 * scaling.scaleFactor,
      width: '100%' as const,
      marginTop: 8 * scaling.scaleFactor,
      justifyContent: "center" as const,
      marginVertical: 10 * scaling.scaleFactor
    },
    button: {
      flex: 1,
      minHeight: 48 * scaling.scaleFactor,
      borderRadius: 12 * scaling.scaleFactor,
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
  };
  const [showDetails, setShowDetails] = useState(false);
  const [details, setDetails] = useState();
  const [modalContent, setModalContent] = useState("");
  const [isReady, setIsReady] = useState(false);
  const [isHiding, setIsHiding] = useState(false);

  // SSE Progress States
  const [isSSEOperation, setIsSSEOperation] = useState(false);
  const [sseProgress, setSSEProgress] = useState(0);
  const [sseStatus, setSSEStatus] = useState('');
  const [sseMessage, setSSEMessage] = useState('');

  const modalRef = useRef<any>(null);
  const wholeRef = useRef<any>(null);
  const autoHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();
  const navigation = useNavigation();

  // Clean up function
  const resetModalState = useCallback(() => {
    setShowDetails(false);
    setDetails(undefined);
    setModalContent("");
    setIsSSEOperation(false);
    setSSEProgress(0);
    setSSEStatus('');
    setSSEMessage('');
    setIsReady(false);
    setIsHiding(false);
    
    // Clear any pending timeouts
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }
  }, []);

  // Hide modal function
  const hideModal = useCallback(() => {
    // Prevent multiple hide calls
    if (isHiding) return;

    setIsHiding(true);

    // Clear timeout first to prevent race conditions
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }

    dispatch({ type: HIDE_AUTH_MODAL });

    // Reset state after a small delay to allow modal animation to complete
    setTimeout(() => {
      resetModalState();
    }, 300);
  }, [dispatch, resetModalState, isHiding]);

  useEffect(() => {
    // Clear any existing timeouts when effect runs
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }

    // Only process if modal is actually visible and has content
    if (!visibleState || !authModal) {
      resetModalState();
      return;
    }

    // Small delay to prevent flash on mobile
    const readyTimeout = setTimeout(() => {
      setIsReady(true);
    }, 50);

    setModalContent(authModal?.modalContent);

    // Check if this is an SSE operation based on modal content or type
    if (authModal?.modalType === 'progress' || authModal?.sseData) {
      setIsSSEOperation(true);

      // Handle SSE data if present
      if (authModal.sseData) {
        const { status, message, progress } = authModal.sseData;
        setSSEStatus(status);
        setSSEMessage(message || '');
        setSSEProgress(progress || 0);

        // Handle completion with proper cleanup - only set timeout if not already set
        if (status === 'completed' && !autoHideTimeoutRef.current) {
          autoHideTimeoutRef.current = setTimeout(() => {
            // Double-check the modal is still in completed state before hiding
            if (visibleState && authModal?.sseData?.status === 'completed') {
              hideModal();
            }
          }, 3000); // Increased timeout to 3 seconds for better UX
        } else if (status === 'error') {
          setIsSSEOperation(false);
          // Don't auto-hide on error, let user dismiss
        }
      }
    } else {
      setIsSSEOperation(false);
      setSSEProgress(0);
      setSSEStatus('');
      setSSEMessage('');

      // For non-SSE success messages, auto-hide after longer delay
      if (authModal?.modalType === 'success' && !autoHideTimeoutRef.current) {
        autoHideTimeoutRef.current = setTimeout(() => {
          if (visibleState && authModal?.modalType === 'success') {
            hideModal();
          }
        }, 4000);
      }
    }

    return () => {
      clearTimeout(readyTimeout);
      // Don't clear autoHideTimeoutRef here as it should persist until modal is hidden
    };
  }, [authModal?.modalContent, authModal?.modalType, authModal?.sseData?.status, authModal?.sseData?.progress, visibleState, hideModal, resetModalState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
      }
    };
  }, []);


  // Helper function to detect errors from content
  const isErrorContent = (content: string | string[] | undefined): boolean => {
    if (!content) return false;
    const contentStr = Array.isArray(content) ? content.join(' ') : content.toString().toLowerCase();
    const errorIndicators = ['403', '401', '404', '500', 'forbidden', 'unauthorized', 'error', 'failed', 'failure'];
    return errorIndicators.some(indicator => contentStr.includes(indicator));
  };

  // Check for error - prioritize error detection
  const isError = authModal?.modalType === 'error' ||
    isErrorContent(modalContent) ||
    isErrorContent(sseMessage) ||
    (isSSEOperation && sseStatus === 'error');

  const returnTypeIcon = () => {
    let Icon: React.JSX.Element;

    if (isSSEOperation && !isError) {
    // Show different icons based on SSE status (only if not an error)
      switch (sseStatus) {
        case 'starting':
        case 'uploading':
        case 'processing':
          Icon = (
            <View style={{ 
              alignItems: 'center', 
              justifyContent: 'center', 
              width: Math.max(100, 120 * scaling.scaleFactor),
              height: Math.max(100, 120 * scaling.scaleFactor)
            }}>
              <Circle 
                size={Math.max(80, 100 * scaling.scaleFactor)} 
                borderWidth={3} 
                borderColor={colors.status.progress} 
                backgroundColor={colors.surface}
              >
                <Progress 
                  size="$4" 
                  value={sseProgress} 
                  max={100}
                  backgroundColor={colors.border}
                >
                  <Progress.Indicator 
                    animation="bouncy" 
                    backgroundColor={colors.status.progress} 
                  />
                </Progress>
              </Circle>
            </View>
          );
          break;
        case 'progress':
          Icon = (
            <YStack alignItems="center" space="$2">
              <Circle 
                size={Math.max(80, 100 * scaling.scaleFactor)} 
                borderWidth={3} 
                borderColor={colors.status.progress} 
                backgroundColor={colors.surface}
              >
                <Text 
                  fontSize={getResponsiveFontSize(16)} 
                  fontWeight="bold" 
                  color={colors.status.progress}
                >
                  {sseProgress}%
                </Text>
              </Circle>
            </YStack>
          );
          break;
        case 'completed':
          Icon = (
            <LottieView 
              autoPlay 
              style={{ 
                width: Math.max(100, 120 * scaling.scaleFactor), 
                height: Math.max(100, 120 * scaling.scaleFactor) 
              }} 
              source={require('@assets/animations/registerSuccess.json')} 
              loop={false} 
            />
          );
          break;
        case 'error':
          Icon = (
            <LottieView 
              autoPlay 
              style={{ 
                width: Math.max(100, 120 * scaling.scaleFactor), 
                height: Math.max(100, 120 * scaling.scaleFactor) 
              }} 
              source={require('@assets/animations/errorIcon.json')} 
              loop={true} 
              speed={1} 
            />
          );
          break;
        default:
          Icon = (
            <YStack alignItems="center" space="$2">
              <Circle 
                size={Math.max(80, 100 * scaling.scaleFactor)} 
                borderWidth={3} 
                borderColor={colors.status.progress} 
                backgroundColor={colors.surface} 
                justifyContent="center" 
                alignItems="center"
              >
                <Progress 
                  size="$4" 
                  value={null} 
                  max={100}
                  backgroundColor={colors.border}
                >
                  <Progress.Indicator 
                    animation="bouncy" 
                    backgroundColor={colors.status.progress} 
                  />
                </Progress>
              </Circle>
              <Text 
                fontSize={getResponsiveFontSize(12)} 
                color={colors.text.secondary}
              >
                Loading...
              </Text>
            </YStack>
          );
      }
    } else if (isError || authModal.modalType === 'error') {
      // Show error icon for any error condition
      Icon = (
        <LottieView 
          autoPlay 
          style={{ 
            width: Math.max(100, 120 * scaling.scaleFactor), 
            height: Math.max(100, 120 * scaling.scaleFactor) 
          }} 
          source={require('@assets/animations/errorIcon.json')} 
          loop={true} 
          speed={1} 
        />
      );
    } else if (authModal.modalType === 'warning') {
      Icon = (
        <LottieView 
          autoPlay 
          style={{ 
            width: Math.max(100, 120 * scaling.scaleFactor), 
            height: Math.max(100, 120 * scaling.scaleFactor) 
          }} 
          source={require('@assets/animations/88006-warning-yellow.json')} 
          loop={true} 
          speed={1} 
        />
      );
    } else {
      Icon = (
        <LottieView 
          autoPlay 
          style={{ 
            width: Math.max(100, 120 * scaling.scaleFactor), 
            height: Math.max(100, 120 * scaling.scaleFactor) 
          }} 
          source={require('@assets/animations/registerSuccess.json')} 
          loop={true} 
        />
      );
    }
    return Icon;
  };

  // Handle navigation logic with proper checks
  useEffect(() => {
    if (authModal?.modalContent?.includes?.("User not found") && visibleState) {
      // Add small delay to prevent immediate navigation and allow user to see the message
      const navigationTimeout = setTimeout(() => {
        hideModal();
        (navigation as any).navigate("Login");
      }, 2000);

      return () => clearTimeout(navigationTimeout);
    }
  }, [authModal?.modalContent, visibleState, navigation, hideModal]);
  // Check if modalContent is an array
  const messages = Array.isArray(modalContent) 
    ? modalContent.join('\n') 
    : modalContent?.toString() || '';

  // Determine the display message - prioritize SSE message for active operations
  const displayMessage = isSSEOperation && sseMessage ? sseMessage : messages;

  // Don't render anything if modal shouldn't be visible or has no content or isn't ready or is hiding
  if (!visibleState || !authModal || (!modalContent && !isSSEOperation) || !isReady || isHiding) {
    return null;
  }

    return (
      <Dialog open={visibleState} onOpenChange={(open) => {
        if (!open) {
      // Only allow closing if it's not an active SSE operation
          if (!isSSEOperation || sseStatus === 'error' || sseStatus === 'completed') {
            hideModal();
          }
        }
      }}>
        <Dialog.Portal style={{ paddingHorizontal: "5%" }}>
          <Dialog.Overlay
            animation="quick"
            opacity={0.6}
            style={{
              zIndex: 1000,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              ...(Platform.OS === 'web' && {
                backdropFilter: 'blur(8px)',
              })
            }}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
            onPress={() => {
              // Only allow backdrop press to close modal if it's not an active SSE operation
              if (!isSSEOperation || sseStatus === 'error' || sseStatus === 'completed') {
                hideModal();
              }
            }}
          />
          <Dialog.Content
            bordered
            elevate
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            x={0}
            scale={1}
            style={[
              {
                padding: 0,
                minWidth: 400,
                maxWidth: 600,
                maxHeight: "85%",
                borderRadius: 24,
                overflow: 'hidden',
                elevation: 12,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: 0.15,
                shadowRadius: 32,
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
                borderWidth: 1,
                borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
              },
              Platform.OS === 'web' && {
                backdropFilter: 'blur(24px)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
              }
            ]}
            opacity={1}
            y={0}
          >
            {/* Header */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 20,
              borderBottomWidth: 1,
              borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              <XStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: isError || authModal?.modalType === 'error'
                    ? (theme ? "#fee2e2" : "#7f1d1d")
                    : sseStatus === 'completed'
                      ? (theme ? "#d1fae5" : "#064e3b")
                      : (theme ? "#f0f9ff" : "#0c4a6e"),
                  borderRadius: 12,
                  padding: 10,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {returnTypeIcon()}
                </View>
                <YStack flex={1}>
                  <Dialog.Title style={{
                    fontSize: 20,
                    fontWeight: '700',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 4,
                    margin: 0,
                  }}>
                    {isSSEOperation
                      ? sseStatus === 'completed'
                        ? 'Operation Complete'
                        : sseStatus === 'error'
                          ? 'Operation Failed'
                          : 'Operation in Progress'
                      : authModal.modalType === 'error'
                        ? 'Error'
                        : authModal.modalType === 'warning'
                          ? 'Warning'
                          : 'Notification'}
                  </Dialog.Title>
                  <Dialog.Description style={{
                    fontSize: 14,
                    color: theme ? '#64748b' : '#94a3b8',
                    fontWeight: '500',
                    margin: 0,
                  }}>
                    {isSSEOperation && sseStatus
                      ? `Status: ${sseStatus.charAt(0).toUpperCase() + sseStatus.slice(1)}`
                      : 'Please review the details below'}
                  </Dialog.Description>
                </YStack>
              </XStack>
            </View>

            {/* Content */}
            <View style={{ padding: 24 }}>
              <ErrorBoundary>
                <YStack gap="$4">
                  {/* Main Message */}
                  <View style={{
                    backgroundColor: theme ? '#f8fafc' : '#374151',
                    borderRadius: 16,
                    padding: 20,
                    borderWidth: 1,
                    borderColor: theme ? '#e5e7eb' : '#4b5563',
                    maxHeight: authModal?.modalType === 'ai-notes' 
                      ? Math.min(windowDimensions.height * 0.5, 500) 
                      : undefined,
                    overflow: 'hidden',
                  }}>
                    {authModal?.modalType === 'ai-notes' ? (
                      <ScrollView
                        style={{ flex: 1 }}
                        nestedScrollEnabled
                        showsVerticalScrollIndicator={true}
                        contentContainerStyle={{ flexGrow: 1 }}
                      >
                        <MarkDown
                          rules={rules}
                          style={{
                            body: {
                              fontSize: 16,
                              lineHeight: 24,
                              fontWeight: '500',
                              color: theme ? '#1e293b' : '#f1f5f9',
                            },
                            paragraph: {
                              fontSize: 16,
                              lineHeight: 24,
                              fontWeight: '500',
                              color: theme ? '#1e293b' : '#f1f5f9',
                              marginVertical: 4,
                            },
                            heading1: {
                              fontSize: 20,
                              fontWeight: 'bold',
                              color: theme ? '#1e293b' : '#f1f5f9',
                              marginVertical: 8,
                            },
                            heading2: {
                              fontSize: 18,
                              fontWeight: 'bold',
                              color: theme ? '#1e293b' : '#f1f5f9',
                              marginVertical: 6,
                            },
                            heading3: {
                              fontSize: 16,
                              fontWeight: '600',
                              color: theme ? '#1e293b' : '#f1f5f9',
                              marginVertical: 4,
                            },
                            code_inline: {
                              backgroundColor: theme ? '#f1f5f9' : '#374151',
                              paddingHorizontal: 4,
                              paddingVertical: 2,
                              borderRadius: 3,
                              fontSize: 14,
                              fontFamily: 'monospace',
                              color: theme ? '#1e293b' : '#f1f5f9',
                            },
                            code_block: {
                              backgroundColor: theme ? '#f8fafc' : '#374151',
                              padding: 8,
                              borderRadius: 4,
                              marginVertical: 4,
                              fontSize: 14,
                              fontFamily: 'monospace',
                              color: theme ? '#1e293b' : '#f1f5f9',
                            },
                            blockquote: {
                              borderLeftWidth: 3,
                              borderLeftColor: theme ? '#cbd5e1' : '#64748b',
                              paddingLeft: 8,
                              marginVertical: 4,
                              backgroundColor: theme ? '#f8fafc' : '#374151',
                            },
                            list_item: {
                              fontSize: 16,
                              lineHeight: 24,
                              color: theme ? '#1e293b' : '#f1f5f9',
                              marginVertical: 2,
                            },
                            strong: {
                              fontWeight: 'bold',
                              color: theme ? '#1e293b' : '#f1f5f9',
                            },
                            em: {
                              fontStyle: 'italic',
                              color: theme ? '#1e293b' : '#f1f5f9',
                            },
                          }}
                        >
                          {displayMessage}
                        </MarkDown>
                      </ScrollView>
                    ) : (
                      <Text style={{
                        fontSize: 16,
                        lineHeight: 24,
                        fontWeight: '500',
                        color: theme ? '#1e293b' : '#f1f5f9',
                        textAlign: 'left',
                      }}>
                        {displayMessage}
                      </Text>
                    )}
                  </View>

                  {/* SSE Progress Bar */}
                  {isSSEOperation && sseStatus === 'progress' && (
                    <View style={{
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      borderRadius: 16,
                      padding: 20,
                      borderWidth: 1,
                      borderColor: theme ? '#e5e7eb' : '#4b5563',
                      gap: 12,
                    }}>
                      <Progress
                        size="$6"
                        value={sseProgress}
                        max={100}
                        backgroundColor={colors.border}
                      >
                        <Progress.Indicator
                          animation="bouncy"
                          backgroundColor={colors.status.progress}
                        />
                      </Progress>
                      <Text style={{
                        fontSize: 14,
                        fontWeight: '600',
                        color: theme ? '#64748b' : '#94a3b8',
                        textAlign: 'center',
                      }}>
                        {sseProgress}% Complete
                      </Text>
                    </View>
                  )}

                  {/* Details Section */}
                  {details && details != undefined && (
                    <View style={{
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      borderRadius: 16,
                      borderWidth: 1,
                      borderColor: theme ? '#e5e7eb' : '#4b5563',
                      overflow: 'hidden',
                    }}>
                      <Pressable
                        onPress={() => setShowDetails(!showDetails)}
                        style={{
                          paddingVertical: 12,
                          paddingHorizontal: 16,
                          backgroundColor: theme ? '#ffffff' : '#1f2937',
                          borderBottomWidth: showDetails ? 1 : 0,
                          borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
                        }}
                      >
                        <Text style={{
                          fontSize: 14,
                          fontWeight: '600',
                          color: colors.status.info,
                          textAlign: 'center',
                        }}>
                          {showDetails ? 'Hide details' : 'Show details'}
                        </Text>
                      </Pressable>

                      {showDetails && (
                        <View style={{
                          padding: 20,
                          gap: 16,
                        }}>
                          <ScrollView
                            style={{ maxHeight: 200 }}
                            nestedScrollEnabled
                          >
                            <Text style={{
                              fontSize: 12,
                              lineHeight: 18,
                              color: theme ? '#64748b' : '#94a3b8',
                              fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
                            }}>
                              {JSON.stringify(details ? details : "No details available for this error", null, 2)}
                            </Text>
                          </ScrollView>
                          <Pressable
                            onPress={() => console.log('handle flag error!')}
                            style={{
                              backgroundColor: colors.status.warning,
                              borderRadius: 12,
                              paddingVertical: 12,
                              paddingHorizontal: 20,
                              alignItems: 'center',
                              marginTop: 8,
                            }}
                          >
                            <Text style={{
                              color: '#ffffff',
                              fontWeight: '600',
                              fontSize: 14,
                            }}>
                              Flag Issue
                            </Text>
                          </Pressable>
                        </View>
                      )}
                    </View>
                  )}
                </YStack>
              </ErrorBoundary>
            </View>

            {/* Footer Actions */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 16,
              borderTopWidth: 1,
              borderTopColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              <XStack gap="$3" justifyContent="flex-end" flexWrap="wrap">
                {/* Cancel button for active SSE operations */}
                {isSSEOperation && sseStatus !== 'completed' && sseStatus !== 'error' && (
                  <Pressable
                    onPress={() => {
                      if (!isHiding) {
                        hideModal();
                      }
                    }}
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#6b7280',
                      minWidth: 100,
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{
                      color: theme ? '#64748b' : '#d1d5db',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Cancel
                    </Text>
                  </Pressable>
                )}

                {/* Upgrade button for usage limits */}
                {!isSSEOperation && messages.includes("Usage limits exceeded") && (
                  <Pressable
                    onPress={() => {
                      if (!isHiding) {
                        hideModal();
                        (navigation as any).navigate("Settings", {
                          screen: "Upgrade"
                        });
                        setTimeout(() => dispatch({ type: "UPGRADE_TAB" }), 200);
                      }
                    }}
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: colors.status.info,
                      minWidth: 100,
                      alignItems: 'center',
                      elevation: 2,
                      shadowColor: colors.status.info,
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.2,
                      shadowRadius: 8,
                    }}
                  >
                    <Text style={{
                      color: '#ffffff',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Upgrade
                    </Text>
                  </Pressable>
                )}

                {/* Close/Done button */}
                {(!isSSEOperation || sseStatus === 'error' || sseStatus === 'completed') && (
                  <Pressable
                    onPress={() => {
                      if (!isHiding) {
                        hideModal();
                      }
                    }}
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: sseStatus === 'completed'
                        ? colors.status.success
                        : (isError || authModal?.modalType === 'error')
                          ? colors.status.error
                          : theme ? '#3b82f6' : '#2563eb',
                      minWidth: 100,
                      alignItems: 'center',
                      elevation: 2,
                      shadowColor: sseStatus === 'completed'
                        ? colors.status.success
                        : colors.status.error,
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.2,
                      shadowRadius: 8,
                    }}
                  >
                    <Text style={{
                      color: '#ffffff',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      {sseStatus === 'completed' ? "Done" : "Close"}
                    </Text>
                  </Pressable>
                )}
              </XStack>
            </View>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    )
};

export default AuthModal